import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Eye, Palette, RefreshCw, Save } from "lucide-react";
import { useEffect, useState } from "react";
import AdminPageLayout from "./AdminPageLayout";
import { apiGet, apiPatch } from "./api";

interface ThemeColors {
  primary: string;
  accent: string;
  green: string;
  logoPurple: string;
  buttonPurple: string;
}

interface HolidaySchedule {
  id?: string;
  name: string;
  date: string; // YYYY-MM-DD
  theme?: ThemeColors;
  enabled: boolean;
}

interface ThemeConfig {
  light: ThemeColors;
  dark: ThemeColors;
  holidays: HolidaySchedule[];
}

// WCAG 대비 비율 계산
function getContrastRatio(color1: string, color2: string): number {
  const getLuminance = (hex: string): number => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;
    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((val) => {
      val = val / 255;
      return val <= 0.03928
        ? val / 12.92
        : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function rgbToHsl(r: number, g: number, b: number): string {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(
    l * 100
  )}%`;
}

function hexToHsl(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return "0 0% 0%";
  return rgbToHsl(rgb.r, rgb.g, rgb.b);
}

export default function ThemeManagement() {
  const [config, setConfig] = useState<ThemeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState<"light" | "dark">("light");
  const [activeTab, setActiveTab] = useState<"colors" | "holidays">("colors");

  // 로컬 테마 상태 (미리보기용)
  const [localTheme, setLocalTheme] = useState<ThemeConfig | null>(null);

  useEffect(() => {
    loadTheme();
  }, []);

  // 미리보기 적용
  useEffect(() => {
    if (localTheme) {
      applyThemeToDOM(localTheme, previewMode);
    }
  }, [localTheme, previewMode]);

  const loadTheme = async () => {
    try {
      const data = await apiGet<ThemeConfig>("/api/admin/theme");
      setConfig(data);
      setLocalTheme(data);
    } catch (error) {
      console.error("Failed to load theme:", error);
      // 기본값 설정 (OZK Blue #60a6fa)
      const defaultConfig: ThemeConfig = {
        light: {
          primary: "#60a6fa", // OZK Blue (뉴스 스타일 포인트 색상)
          accent: "#FF7A00",
          green: "#10B981",
          logoPurple: "#60a6fa", // OZK Blue로 통일
          buttonPurple: "#60a6fa", // OZK Blue로 통일
        },
        dark: {
          primary: "#60a6fa", // OZK Blue (뉴스 스타일 포인트 색상)
          accent: "#FF8A3D",
          green: "#34D399",
          logoPurple: "#60a6fa", // OZK Blue로 통일
          buttonPurple: "#60a6fa", // OZK Blue로 통일
        },
        holidays: [],
      };
      setConfig(defaultConfig);
      setLocalTheme(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  const applyThemeToDOM = (theme: ThemeConfig, mode: "light" | "dark") => {
    const root = document.documentElement;
    const colors = mode === "light" ? theme.light : theme.dark;

    // CSS 변수 업데이트
    root.style.setProperty("--brand-primary", hexToHsl(colors.primary));
    root.style.setProperty("--brand-accent", hexToHsl(colors.accent));
    root.style.setProperty("--brand-green", hexToHsl(colors.green));
    root.style.setProperty("--logo-purple", hexToHsl(colors.logoPurple));
    root.style.setProperty("--button-purple", hexToHsl(colors.buttonPurple));

    // 다크모드 클래스 토글
    if (mode === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  };

  const updateColor = (
    key: keyof ThemeColors,
    value: string,
    mode: "light" | "dark"
  ) => {
    if (!localTheme) return;

    const updated = {
      ...localTheme,
      [mode]: {
        ...localTheme[mode],
        [key]: value,
      },
    };
    setLocalTheme(updated);
  };

  const saveTheme = async () => {
    if (!localTheme) return;
    setSaving(true);
    try {
      await apiPatch("/api/admin/theme", localTheme);
      setConfig(localTheme);
      // 실제 DOM에도 적용
      applyThemeToDOM(localTheme, previewMode);
    } catch (error) {
      console.error("Failed to save theme:", error);
      alert("테마 저장 실패");
    } finally {
      setSaving(false);
    }
  };

  const resetTheme = () => {
    if (config) {
      setLocalTheme(config);
      applyThemeToDOM(config, previewMode);
    }
  };

  const addHoliday = () => {
    if (!localTheme) return;
    const newHoliday: HolidaySchedule = {
      name: "",
      date: new Date().toISOString().split("T")[0],
      enabled: true,
    };
    setLocalTheme({
      ...localTheme,
      holidays: [...localTheme.holidays, newHoliday],
    });
  };

  const updateHoliday = (index: number, updates: Partial<HolidaySchedule>) => {
    if (!localTheme) return;
    const updated = [...localTheme.holidays];
    updated[index] = { ...updated[index], ...updates };
    setLocalTheme({ ...localTheme, holidays: updated });
  };

  const removeHoliday = (index: number) => {
    if (!localTheme) return;
    setLocalTheme({
      ...localTheme,
      holidays: localTheme.holidays.filter((_, i) => i !== index),
    });
  };

  if (!localTheme) return null;

  const currentColors = localTheme[previewMode];
  const contrastWithWhite = getContrastRatio(currentColors.primary, "#FFFFFF");
  const contrastWithBlack = getContrastRatio(currentColors.primary, "#000000");

  return (
    <AdminPageLayout
      title="Theme Management"
      description="Manage theme colors and holiday schedules"
      icon={<Palette className="w-8 h-8" />}
      loading={loading}
      headerActions={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setPreviewMode(previewMode === "light" ? "dark" : "light")
            }
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewMode === "light" ? "다크" : "라이트"} 모드 미리보기
          </Button>
          <Button variant="outline" size="sm" onClick={resetTheme}>
            <RefreshCw className="w-4 h-4 mr-2" />
            초기화
          </Button>
          <Button size="sm" onClick={saveTheme} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "저장 중..." : "저장"}
          </Button>
        </>
      }
    >
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              테마 컬러 관리
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setPreviewMode(previewMode === "light" ? "dark" : "light")
                }
              >
                <Eye className="w-4 h-4 mr-2" />
                {previewMode === "light" ? "다크" : "라이트"} 모드 미리보기
              </Button>
              <Button variant="outline" size="sm" onClick={resetTheme}>
                <RefreshCw className="w-4 h-4 mr-2" />
                초기화
              </Button>
              <Button size="sm" onClick={saveTheme} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "저장 중..." : "저장"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
            <TabsList>
              <TabsTrigger value="colors">컬러</TabsTrigger>
              <TabsTrigger value="holidays">공휴일 스케줄</TabsTrigger>
            </TabsList>

            <TabsContent value="colors" className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Primary */}
                <div className="space-y-2">
                  <Label>Primary (브랜드 메인)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentColors.primary}
                      onChange={(e) =>
                        updateColor("primary", e.target.value, previewMode)
                      }
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={currentColors.primary}
                      onChange={(e) =>
                        updateColor("primary", e.target.value, previewMode)
                      }
                      className="flex-1 font-mono"
                    />
                  </div>
                  <div
                    className="h-12 rounded border flex items-center justify-center text-sm font-semibold"
                    style={{
                      backgroundColor: currentColors.primary,
                      color: contrastWithWhite > 4.5 ? "#FFFFFF" : "#000000",
                    }}
                  >
                    샘플 텍스트
                  </div>
                </div>

                {/* Accent */}
                <div className="space-y-2">
                  <Label>Accent (액션/배지)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentColors.accent}
                      onChange={(e) =>
                        updateColor("accent", e.target.value, previewMode)
                      }
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={currentColors.accent}
                      onChange={(e) =>
                        updateColor("accent", e.target.value, previewMode)
                      }
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>

                {/* Green */}
                <div className="space-y-2">
                  <Label>Green (Trust/Verified)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentColors.green}
                      onChange={(e) =>
                        updateColor("green", e.target.value, previewMode)
                      }
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={currentColors.green}
                      onChange={(e) =>
                        updateColor("green", e.target.value, previewMode)
                      }
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>

                {/* Logo Purple */}
                <div className="space-y-2">
                  <Label>Logo Purple (로고 색상)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentColors.logoPurple}
                      onChange={(e) =>
                        updateColor("logoPurple", e.target.value, previewMode)
                      }
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={currentColors.logoPurple}
                      onChange={(e) =>
                        updateColor("logoPurple", e.target.value, previewMode)
                      }
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>

                {/* Button Purple */}
                <div className="space-y-2">
                  <Label>Button Purple (버튼 색상)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentColors.buttonPurple}
                      onChange={(e) =>
                        updateColor("buttonPurple", e.target.value, previewMode)
                      }
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={currentColors.buttonPurple}
                      onChange={(e) =>
                        updateColor("buttonPurple", e.target.value, previewMode)
                      }
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* 대비 검사 */}
              <Card className="bg-muted">
                <CardHeader>
                  <CardTitle className="text-sm">대비 검사 (WCAG)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Primary vs 흰색:</span>
                    <Badge
                      variant={
                        contrastWithWhite >= 4.5 ? "default" : "destructive"
                      }
                    >
                      {contrastWithWhite.toFixed(2)}:1{" "}
                      {contrastWithWhite >= 4.5 ? "✅" : "❌"}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Primary vs 검은색:</span>
                    <Badge
                      variant={
                        contrastWithBlack >= 4.5 ? "default" : "destructive"
                      }
                    >
                      {contrastWithBlack.toFixed(2)}:1{" "}
                      {contrastWithBlack >= 4.5 ? "✅" : "❌"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    WCAG AA 기준: 최소 4.5:1 필요
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="holidays" className="space-y-4 mt-6">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">공휴일 테마 스케줄</h3>
                <Button size="sm" onClick={addHoliday}>
                  <Calendar className="w-4 h-4 mr-2" />
                  추가
                </Button>
              </div>

              <div className="space-y-4">
                {localTheme.holidays.map((holiday, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <Label>이름</Label>
                          <Input
                            value={holiday.name}
                            onChange={(e) =>
                              updateHoliday(index, { name: e.target.value })
                            }
                            placeholder="예: 크리스마스"
                          />
                        </div>
                        <div>
                          <Label>날짜</Label>
                          <Input
                            type="date"
                            value={holiday.date}
                            onChange={(e) =>
                              updateHoliday(index, { date: e.target.value })
                            }
                          />
                        </div>
                        <div className="flex items-end">
                          <Label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={holiday.enabled}
                              onChange={(e) =>
                                updateHoliday(index, {
                                  enabled: e.target.checked,
                                })
                              }
                            />
                            활성화
                          </Label>
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => removeHoliday(index)}
                          >
                            삭제
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {localTheme.holidays.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    공휴일 스케줄이 없습니다. 추가 버튼을 클릭하여 추가하세요.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
